import { RequestStatus } from '../../data/constants';
import { logStudioHomeError } from '../logging';
import {
  getStudioHomeData,
  sendRequestForCourseCreator,
  handleCourseNotification,
  getStudioHomeCourses,
  getStudioHomeLibraries,
  getStudioHomeCoursesV2,
  getCompanyManagementPortalGeneralData,
  getCompanyManagementPortalSpecificData,
  createCompany,
  createCompanyCatalog,
  updateCompanyFriendlyName,
  updateCompanySize,
  updateCompanyWebsite,
  updateCourseAuthoringOrg,
  addCustomCsvFields,
  removeCustomCsvFields,
  addCustomAdminPortalFeatures,
  removeCustomAdminPortalFeatures,
  registerBulkUsers,
  addCompanyAdmins,
  removeCompanyAdmins,
  updatePrimaryAdmin,
  getCompanyCatalog,
  addLegacyLicenses,
  removeLegacyLicenses,
  patchCompanyCatalog,
  deleteCompanyCatalogItems,
  addCompanyCatalogItems,
  getPlatforms,
  createPlatform,
  updatePlatform,
  getPrograms,
  createProgram,
  updateProgram,
  deleteProgram,
  triggerAdminPortalCacheRefresh,
  runCourseAggregation,
  COMPANY_CACHED_DATA_ENDPOINTS,
  PAGINATED_COMPANY_CACHED_DATA_ENDPOINTS,
  getCompanyCachedDataManifest,
  getCompanyCachedDataPage,
  getCompanyCachedDataAllPages,
  getCompanyEmployees,
  getCompanyEmployeePage,
  getBulkLearnerEnrollments,
  removeEnterpriseLearner,
} from './api';
import {
  fetchStudioHomeDataSuccess,
  fetchCourseDataSuccess,
  updateLoadingStatuses,
  updateSavingStatuses,
  fetchLibraryDataSuccess,
  fetchCourseDataSuccessV2,
  fetchCompanyManagementPortalDataSuccess,
  fetchCompanyManagementSpecificDataSuccess,
  fetchCompanyCatalogSuccess,
  fetchLegacyLicensesSuccess,
  fetchPlatformDataSuccess,
  createPlatformSuccess,
  updatePlatformSuccess,
  fetchProgramDataSuccess,
  createProgramSuccess,
  updateProgramSuccess,
  deleteProgramSuccess,
  fetchFullCourseListSuccess,
  initializeCompanyCachedData,
  storeCompanyCachedDataManifest,
  storeCompanyCachedDataPage,
  storeCompanyCachedDataValue,
  updateCompanyCachedDataEndpointStatus,
  fetchSpecificCompanyCachedDataFailure,
  fetchSpecificCompanyCachedDataSuccess,
  syncSelectedCompanyCachedData,
  fetchCompanyEmployeesSuccess,
  fetchCompanyEmployeePageSuccess,
  storeCompanyEmployeePage,
  clearCompanyEmployeePages,
  fetchCompanyAdminsSuccess,
  appendCompanyAdmins,
  pruneLearnerEnrollmentsForOrg,
  invalidateLearnerEnrollments,
  initializeLearnerEnrollmentsForOrg,
  setLearnerEnrollmentBatchLoading,
  storeLearnerEnrollmentBatchSuccess,
  storeLearnerEnrollmentBatchFailure,
} from './slice';

const COMPANY_CACHED_DATA_HYDRATION_BATCH_SIZE = 3;

// Keep learner-enrollment bulk requests small — measured ~4-6s for 250 learners with many
// course enrollments each, so batches of 25 are used to keep each request fast, loaded
// sequentially in the background (similar in spirit to the old sbl-cached-data prefetch).
const LEARNER_ENROLLMENTS_BATCH_SIZE = 25;

const chunkArray = (items, size) => {
  const chunks = [];
  for (let i = 0; i < items.length; i += size) {
    chunks.push(items.slice(i, i + size));
  }
  return chunks;
};

let companyCachedDataRequestCounter = 0;
const inFlightCompanyCachedDataPageRequests = new Map();
const inFlightCompanyEmployeePageRequests = new Map();

const getCompanyCachedDataRequestId = (companyName) => {
  companyCachedDataRequestCounter += 1;
  return `${companyName}-${companyCachedDataRequestCounter}`;
};

const loadCompanyEmployeePage = (companyName, page, pageSize) => {
  const key = `${companyName}:${page}:${pageSize}`;
  if (!inFlightCompanyEmployeePageRequests.has(key)) {
    const request = getCompanyEmployeePage(companyName, page, pageSize)
      .finally(() => inFlightCompanyEmployeePageRequests.delete(key));
    inFlightCompanyEmployeePageRequests.set(key, request);
  }
  return inFlightCompanyEmployeePageRequests.get(key);
};

const COMPANY_CACHED_DATA_ENDPOINT_KEYS = Object.keys(COMPANY_CACHED_DATA_ENDPOINTS);

const COMPANY_CACHED_DATA_ENDPOINT_FETCH_ORDER = [
  'totalLearningOverview',
  'skillsDashboard',
  'skillsDashboardAssignment',
  'skillsDashboardAssignmentType',
  'unregisteredLearners',
  'allReflections',
  'assessments',
  'assessmentsV2',
  'completionBySkill',
].filter((endpoint) => COMPANY_CACHED_DATA_ENDPOINT_KEYS.includes(endpoint));

const chunk = (items, size) => {
  const batches = [];
  for (let index = 0; index < items.length; index += size) {
    batches.push(items.slice(index, index + size));
  }
  return batches;
};

const getEndpointTotalPages = (manifest) => {
  const totalPages = Number(manifest?.total_pages);
  return Number.isInteger(totalPages) && totalPages > 0 ? totalPages : 1;
};

const getCompanyCachedDataEntry = (getState, companyName) => (
  getState()?.studioHome?.companyCachedDataByOrg?.[companyName] || null
);

const getFetchedCompanyCachedDataPageSet = (getState, companyName, endpoint) => {
  const pages = getCompanyCachedDataEntry(getState, companyName)?.pages?.[endpoint] || {};
  return new Set(
    Object.keys(pages)
      .map((pageNumber) => Number(pageNumber))
      .filter((pageNumber) => Number.isInteger(pageNumber) && pageNumber > 0),
  );
};

async function fetchCompanyCachedDataManifestIfNeeded(dispatch, getState, companyName, endpoint, requestId) {
  const existingManifest = getCompanyCachedDataEntry(getState, companyName)?.manifests?.[endpoint];
  if (existingManifest) {
    return existingManifest;
  }

  const manifest = await getCompanyCachedDataManifest(companyName, endpoint);
  dispatch(storeCompanyCachedDataManifest({
    companyName,
    endpoint,
    manifest,
    requestId,
  }));
  return manifest;
}

async function fetchCompanyCachedDataEndpointPageIfNeeded(
  dispatch,
  getState,
  companyName,
  endpoint,
  pageNumber,
  requestId,
) {
  const fetchedPageSet = getFetchedCompanyCachedDataPageSet(getState, companyName, endpoint);
  if (fetchedPageSet.has(pageNumber)) {
    return null;
  }

  const requestKey = `${companyName}:${endpoint}:${pageNumber}`;
  const inFlightRequest = inFlightCompanyCachedDataPageRequests.get(requestKey);
  if (inFlightRequest) {
    return inFlightRequest;
  }

  const requestPromise = getCompanyCachedDataPage(companyName, endpoint, pageNumber)
    .then((response) => {
      if (response.manifest) {
        dispatch(storeCompanyCachedDataManifest({
          companyName,
          endpoint,
          manifest: response.manifest,
          requestId,
        }));
      }
      dispatch(storeCompanyCachedDataPage({
        companyName,
        endpoint,
        pageNumber,
        data: response.data ?? [],
        requestId,
      }));
      dispatch(syncSelectedCompanyCachedData(companyName));
      return response;
    })
    .finally(() => {
      inFlightCompanyCachedDataPageRequests.delete(requestKey);
    });

  inFlightCompanyCachedDataPageRequests.set(requestKey, requestPromise);
  return requestPromise;
}

async function hydratePaginatedCompanyCachedDataEndpoint(
  dispatch,
  getState,
  companyName,
  endpoint,
  requestId,
  options = {},
) {
  const { complete = true } = options;
  dispatch(updateCompanyCachedDataEndpointStatus({
    companyName,
    endpoint,
    status: RequestStatus.IN_PROGRESS,
    requestId,
  }));

  const manifest = await fetchCompanyCachedDataManifestIfNeeded(
    dispatch,
    getState,
    companyName,
    endpoint,
    requestId,
  );
  await fetchCompanyCachedDataEndpointPageIfNeeded(
    dispatch,
    getState,
    companyName,
    endpoint,
    1,
    requestId,
  );
  dispatch(updateCompanyCachedDataEndpointStatus({
    companyName,
    endpoint,
    status: RequestStatus.PARTIAL,
    requestId,
  }));

  const totalPages = getEndpointTotalPages(manifest);
  if (totalPages <= 1) {
    dispatch(updateCompanyCachedDataEndpointStatus({
      companyName,
      endpoint,
      status: RequestStatus.SUCCESSFUL,
      requestId,
    }));
    return { endpoint };
  }

  if (!complete) {
    return { endpoint };
  }

  const remainingPages = [];
  for (let pageNumber = 2; pageNumber <= totalPages; pageNumber += 1) {
    remainingPages.push(pageNumber);
  }

  const pageBatches = chunk(remainingPages, COMPANY_CACHED_DATA_HYDRATION_BATCH_SIZE);
  for (const pageBatch of pageBatches) {
    // eslint-disable-next-line no-await-in-loop
    await Promise.all(pageBatch.map((pageNumber) => fetchCompanyCachedDataEndpointPageIfNeeded(
      dispatch,
      getState,
      companyName,
      endpoint,
      pageNumber,
      requestId,
    )));
  }

  dispatch(updateCompanyCachedDataEndpointStatus({
    companyName,
    endpoint,
    status: RequestStatus.SUCCESSFUL,
    requestId,
  }));

  return { endpoint };
}

async function hydrateNonPaginatedCompanyCachedDataEndpoint(dispatch, companyName, endpoint, requestId) {
  dispatch(updateCompanyCachedDataEndpointStatus({
    companyName,
    endpoint,
    status: RequestStatus.IN_PROGRESS,
    requestId,
  }));

  const response = await getCompanyCachedDataAllPages(companyName, endpoint);
  dispatch(storeCompanyCachedDataValue({
    companyName,
    endpoint,
    data: response.data ?? [],
    manifest: response.manifest ?? null,
    requestId,
  }));
  dispatch(syncSelectedCompanyCachedData(companyName));
  dispatch(updateCompanyCachedDataEndpointStatus({
    companyName,
    endpoint,
    status: RequestStatus.SUCCESSFUL,
    requestId,
  }));

  return { endpoint };
}

function fetchCompanyCachedDataEndpointPage(companyName, endpoint, pageNumber) {
  return async (dispatch, getState) => {
    const cachedDataEntry = getCompanyCachedDataEntry(getState, companyName);
    const requestId = cachedDataEntry?.activeRequestId;

    if (!companyName || !endpoint || !pageNumber || !requestId) {
      return null;
    }

    return fetchCompanyCachedDataEndpointPageIfNeeded(
      dispatch,
      getState,
      companyName,
      endpoint,
      pageNumber,
      requestId,
    );
  };
}

function fetchStudioHomeData(search, hasHomeData, requestParams = {}, isPaginationEnabled = false) {
  return async (dispatch) => {
    dispatch(updateLoadingStatuses({ studioHomeLoadingStatus: RequestStatus.IN_PROGRESS }));
    dispatch(updateLoadingStatuses({ courseLoadingStatus: RequestStatus.IN_PROGRESS }));

    if (!hasHomeData) {
      try {
        const studioHomeData = await getStudioHomeData();
        dispatch(fetchStudioHomeDataSuccess(studioHomeData));
        dispatch(updateLoadingStatuses({ studioHomeLoadingStatus: RequestStatus.SUCCESSFUL }));
      } catch (error) {
        dispatch(updateLoadingStatuses({ studioHomeLoadingStatus: RequestStatus.FAILED }));
        return;
      }
    }
    try {
      if (isPaginationEnabled) {
        const coursesData = await getStudioHomeCoursesV2(search || '', requestParams);
        dispatch(fetchCourseDataSuccessV2(coursesData));
      } else {
        const coursesData = await getStudioHomeCourses(search || '');
        dispatch(fetchCourseDataSuccess(coursesData));
      }

      dispatch(updateLoadingStatuses({ courseLoadingStatus: RequestStatus.SUCCESSFUL }));
    } catch (error) {
      dispatch(updateLoadingStatuses({ courseLoadingStatus: RequestStatus.FAILED }));
    }
  };
}

function fetchLibraryData() {
  return async (dispatch) => {
    dispatch(updateLoadingStatuses({ libraryLoadingStatus: RequestStatus.IN_PROGRESS }));

    try {
      const libraryData = await getStudioHomeLibraries();
      dispatch(fetchLibraryDataSuccess(libraryData));
      dispatch(updateLoadingStatuses({ libraryLoadingStatus: RequestStatus.SUCCESSFUL }));
    } catch (error) {
      dispatch(updateLoadingStatuses({ libraryLoadingStatus: RequestStatus.FAILED }));
    }
  };
}

function fetchFullCourseList() {
  return async (dispatch) => {
    dispatch(updateLoadingStatuses({ fullCourseListLoadingStatus: RequestStatus.IN_PROGRESS }));

    try {
      // Fetch all courses by iterating through pages
      const allCourses = [];
      let currentPage = 1;
      let hasMore = true;

      while (hasMore) {
        // eslint-disable-next-line no-await-in-loop
        const response = await getStudioHomeCoursesV2('', { page: currentPage });

        if (response.results?.courses) {
          allCourses.push(...response.results.courses);
          // Check if there are more pages
          hasMore = response.next !== null;
          currentPage += 1;
        } else {
          hasMore = false;
        }
      }

      // Transform courses into the format used by the Programs tab
      const courseList = allCourses.map((course) => ({
        courseId: course.courseKey || course.courseId || course.id || course.key,
        displayName: course.displayName || course.name || course.title || course.courseKey || course.id,
      }));

      dispatch(fetchFullCourseListSuccess(courseList));
      dispatch(updateLoadingStatuses({ fullCourseListLoadingStatus: RequestStatus.SUCCESSFUL }));
    } catch (error) {
      dispatch(updateLoadingStatuses({ fullCourseListLoadingStatus: RequestStatus.FAILED }));
    }
  };
}

function fetchCompanyManagementGeneralData() {
  return async (dispatch) => {
    dispatch(updateLoadingStatuses({ companyManagementPortalLoadingStatus: RequestStatus.IN_PROGRESS }));

    try {
      const companyManagementData = await getCompanyManagementPortalGeneralData();
      dispatch(fetchCompanyManagementPortalDataSuccess(companyManagementData));
      dispatch(updateLoadingStatuses({ companyManagementPortalLoadingStatus: RequestStatus.SUCCESSFUL }));
    } catch (error) {
      dispatch(updateLoadingStatuses({ companyManagementPortalLoadingStatus: RequestStatus.FAILED }));
    }
  };
}

function fetchCompanyManagementSpecificData(companyName) {
  return async (dispatch) => {
    dispatch(updateLoadingStatuses({ companyManagementPortalLoadingStatus: RequestStatus.IN_PROGRESS }));

    try {
      const companySpecificData = await getCompanyManagementPortalSpecificData(companyName);
      dispatch(fetchCompanyManagementSpecificDataSuccess(companySpecificData));
      dispatch(updateLoadingStatuses({ companyManagementPortalLoadingStatus: RequestStatus.SUCCESSFUL }));
    } catch (error) {
      dispatch(updateLoadingStatuses({ companyManagementPortalLoadingStatus: RequestStatus.FAILED }));
    }
  };
}

function createNewCompany(companyData) {
  return async (dispatch) => {
    dispatch(updateSavingStatuses({ createCompanySavingStatus: RequestStatus.PENDING }));

    try {
      const newCompany = await createCompany(companyData);

      // Create the catalog for the new company
      const catalogData = {
        company_name: newCompany.company_name,
        skill_select: {
          enabled: true,
          total_available: null,
          limit: 1,
        },
        catalog: [],
        ...(companyData.edx_course_org && { edx_course_org: companyData.edx_course_org }),
      };

      await createCompanyCatalog(catalogData);

      // Refresh the company list
      const companyManagementData = await getCompanyManagementPortalGeneralData();
      dispatch(fetchCompanyManagementPortalDataSuccess(companyManagementData));

      dispatch(updateSavingStatuses({ createCompanySavingStatus: RequestStatus.SUCCESSFUL }));
      return newCompany;
    } catch (error) {
      dispatch(
        updateSavingStatuses({
          createCompanySavingStatus: RequestStatus.FAILED,
        }),
      );
      throw error;
    }
  };
}

function handleDeleteNotificationQuery(url) {
  return async (dispatch) => {
    dispatch(updateSavingStatuses({ deleteNotificationSavingStatus: RequestStatus.PENDING }));

    try {
      await handleCourseNotification(url);
      dispatch(updateSavingStatuses({ deleteNotificationSavingStatus: RequestStatus.SUCCESSFUL }));
    } catch (error) {
      dispatch(updateSavingStatuses({ deleteNotificationSavingStatus: RequestStatus.FAILED }));
    }
  };
}

function requestCourseCreatorQuery() {
  return async (dispatch) => {
    dispatch(updateSavingStatuses({ courseCreatorSavingStatus: RequestStatus.PENDING }));

    try {
      await sendRequestForCourseCreator();
      dispatch(updateSavingStatuses({ courseCreatorSavingStatus: RequestStatus.SUCCESSFUL }));
      return true;
    } catch (error) {
      dispatch(updateSavingStatuses({ courseCreatorSavingStatus: RequestStatus.FAILED }));
      return false;
    }
  };
}

// Company Management Portal editing thunks
function updateCompanyField(companyName, field, value) {
  return async (dispatch) => {
    const savingKey = `updateCompany${field.charAt(0).toUpperCase() + field.slice(1)}SavingStatus`;
    dispatch(updateSavingStatuses({ [savingKey]: RequestStatus.PENDING }));

    try {
      let result;
      switch (field) {
        case 'friendlyName':
          result = await updateCompanyFriendlyName(companyName, value);
          break;
        case 'size':
          result = await updateCompanySize(companyName, value);
          break;
        case 'website':
          result = await updateCompanyWebsite(companyName, value);
          break;
        case 'edxCourseOrg':
          result = await updateCourseAuthoringOrg(companyName, value);
          break;
        default:
          throw new Error(`Unknown field: ${field}`);
      }

      // Refresh the specific company data
      const companySpecificData = await getCompanyManagementPortalSpecificData(companyName);
      dispatch(fetchCompanyManagementSpecificDataSuccess(companySpecificData));

      // For edxCourseOrg, also fetch the company catalog since that's where it comes from
      if (field === 'edxCourseOrg') {
        try {
          // eslint-disable-next-line @typescript-eslint/no-use-before-define
          await dispatch(fetchCompanyCatalog(companyName));
        } catch (error) {
          logStudioHomeError('studio_home_company_catalog_refresh_failed', error);
        }
      }

      // Also refresh the general company list to update dropdown/search
      const companyManagementData = await getCompanyManagementPortalGeneralData();
      dispatch(fetchCompanyManagementPortalDataSuccess(companyManagementData));

      dispatch(updateSavingStatuses({ [savingKey]: RequestStatus.SUCCESSFUL }));
      return result;
    } catch (error) {
      dispatch(updateSavingStatuses({ [savingKey]: RequestStatus.FAILED }));
      throw error;
    }
  };
}

function fetchPlatformData() {
  return async (dispatch) => {
    dispatch(updateLoadingStatuses({ platformLoadingStatus: RequestStatus.IN_PROGRESS }));

    try {
      const platformData = await getPlatforms('*');
      dispatch(fetchPlatformDataSuccess(platformData.platforms));
      dispatch(updateLoadingStatuses({ platformLoadingStatus: RequestStatus.SUCCESSFUL }));
    } catch (error) {
      dispatch(updateLoadingStatuses({ platformLoadingStatus: RequestStatus.FAILED }));
    }
  };
}

function updateCustomCsvFields(companyName, action, fields) {
  return async (dispatch) => {
    dispatch(updateSavingStatuses({ updateCustomCsvFieldsSavingStatus: RequestStatus.PENDING }));

    try {
      if (action === 'add') {
        await addCustomCsvFields(companyName, fields);
      } else if (action === 'remove') {
        await removeCustomCsvFields(companyName, fields);
      }

      // Refresh the specific company data
      const companySpecificData = await getCompanyManagementPortalSpecificData(companyName);
      dispatch(fetchCompanyManagementSpecificDataSuccess(companySpecificData));

      dispatch(updateSavingStatuses({ updateCustomCsvFieldsSavingStatus: RequestStatus.SUCCESSFUL }));
    } catch (error) {
      dispatch(updateSavingStatuses({ updateCustomCsvFieldsSavingStatus: RequestStatus.FAILED }));
      throw error;
    }
  };
}

function updateCustomAdminPortalFeatures(companyName, action, features) {
  return async (dispatch) => {
    dispatch(updateSavingStatuses({ updateCustomAdminPortalFeaturesSavingStatus: RequestStatus.PENDING }));

    try {
      if (action === 'add') {
        await addCustomAdminPortalFeatures(companyName, features);
      } else if (action === 'remove') {
        await removeCustomAdminPortalFeatures(companyName, features);
      }

      // Refresh the specific company data
      const companySpecificData = await getCompanyManagementPortalSpecificData(companyName);
      dispatch(fetchCompanyManagementSpecificDataSuccess(companySpecificData));

      dispatch(updateSavingStatuses({ updateCustomAdminPortalFeaturesSavingStatus: RequestStatus.SUCCESSFUL }));
    } catch (error) {
      dispatch(updateSavingStatuses({ updateCustomAdminPortalFeaturesSavingStatus: RequestStatus.FAILED }));
      throw error;
    }
  };
}

function manageCompanyAdmins(companyName, action, adminData) {
  return async (dispatch) => {
    dispatch(updateSavingStatuses({ manageCompanyAdminsSavingStatus: RequestStatus.PENDING }));

    try {
      if (action === 'add') {
        // First register the user if not exists
        if (adminData.newUser) {
          await registerBulkUsers({
            b2b_org: companyName,
            employees: [{
              email: adminData.email,
              password: adminData.password,
              first_name: adminData.firstName || 'New',
              last_name: adminData.lastName || 'Admin',
              is_alumni: false,
              must_update_pass: adminData.must_update_pass !== undefined ? adminData.must_update_pass : true,
              enterprise: {
                company_name: companyName,
                friendly_name: adminData.companyFriendlyName,
                is_admin: true,
                metadata: adminData.metadata || {},
                job_title: adminData.jobTitle || 'Administrator',
                is_company_catalog: adminData.is_company_catalog !== undefined ? adminData.is_company_catalog : false,
                edx_course_org: companyName,
              },
            }],
          });
        }
        // Then add as admin
        await addCompanyAdmins(companyName, adminData.email);
      } else if (action === 'remove') {
        await removeCompanyAdmins(companyName, adminData.email);

        if (adminData.removeCompletely) {
          await removeEnterpriseLearner(companyName, adminData.email);
        }
      } else if (action === 'setPrimary') {
        await updatePrimaryAdmin(companyName, adminData.email);
      }

      // Refresh the specific company data and employee list, then re-derive admins
      const companySpecificData = await getCompanyManagementPortalSpecificData(companyName);
      dispatch(fetchCompanyManagementSpecificDataSuccess(companySpecificData));
      dispatch(clearCompanyEmployeePages(companyName));
      const freshEmployees = await getCompanyEmployees(companyName);
      dispatch(fetchCompanyEmployeesSuccess(freshEmployees));
      dispatch(pruneLearnerEnrollmentsForOrg({
        companyName,
        usernames: freshEmployees.map((employee) => employee.username).filter(Boolean),
      }));
      const freshAdmins = Array.isArray(freshEmployees)
        ? freshEmployees.filter((e) => e.is_admin === true)
        : [];
      dispatch(fetchCompanyAdminsSuccess(freshAdmins));

      dispatch(updateSavingStatuses({ manageCompanyAdminsSavingStatus: RequestStatus.SUCCESSFUL }));
    } catch (error) {
      dispatch(updateSavingStatuses({ manageCompanyAdminsSavingStatus: RequestStatus.FAILED }));
      throw error;
    }
  };
}

/**
 * Company Catalog Management Thunks
 */

/**
 * Fetch company catalog data - stores complete API response
 * Returns empty object on 404 (company not migrated to new catalog system)
 */
function fetchCompanyCatalog(companyName) {
  return async (dispatch) => {
    dispatch(updateSavingStatuses({ fetchCompanyCatalogStatus: RequestStatus.IN_PROGRESS }));

    try {
      const catalogData = await getCompanyCatalog(companyName);
      dispatch(fetchCompanyCatalogSuccess(catalogData));
      dispatch(updateSavingStatuses({ fetchCompanyCatalogStatus: RequestStatus.SUCCESSFUL }));
      return catalogData;
    } catch (error) {
      // Handle 404 gracefully - company catalog doesn't exist (not migrated yet)
      // Return empty object so components can infer "not a company catalog organization"
      if (error.response?.status === 404 || error.customAttributes?.httpErrorStatus === 404) {
        dispatch(fetchCompanyCatalogSuccess({}));
        dispatch(updateSavingStatuses({ fetchCompanyCatalogStatus: RequestStatus.SUCCESSFUL }));
        return {};
      }

      // For other errors, fail as normal
      logStudioHomeError('studio_home_company_catalog_fetch_failed', error);
      dispatch(updateSavingStatuses({ fetchCompanyCatalogStatus: RequestStatus.FAILED }));
      throw error;
    }
  };
}

/**
 * Fetch legacy license bundles from company details
 */
function fetchLegacyLicenses(companyName) {
  return async (dispatch) => {
    dispatch(updateSavingStatuses({ fetchLegacyLicensesStatus: RequestStatus.IN_PROGRESS }));

    try {
      const companyDetails = await getCompanyManagementPortalSpecificData(companyName);
      const legacyData = {
        companyName,
        bundles: companyDetails.bundle_list || [],
      };
      dispatch(fetchLegacyLicensesSuccess(legacyData));
      dispatch(updateSavingStatuses({ fetchLegacyLicensesStatus: RequestStatus.SUCCESSFUL }));
      return legacyData;
    } catch (error) {
      logStudioHomeError('studio_home_legacy_licenses_fetch_failed', error);
      dispatch(updateSavingStatuses({ fetchLegacyLicensesStatus: RequestStatus.FAILED }));
      throw error;
    }
  };
}

/**
 * Convenience wrapper to fetch all company license data at once
 */
function fetchAllCompanyLicenseData(companyName, platformKey) {
  return async (dispatch) => {
    try {
      await Promise.all([
        dispatch(fetchCompanyCatalog(companyName)),
        dispatch(fetchLegacyLicenses(companyName)),
        // eslint-disable-next-line @typescript-eslint/no-use-before-define
        platformKey ? dispatch(fetchProgramData(platformKey)) : Promise.resolve(),
      ]);
    } catch (error) {
      logStudioHomeError('studio_home_company_license_data_fetch_failed', error);
      throw error;
    }
  };
}

function createPlatformThunk(platformData) {
  return async (dispatch) => {
    try {
      const newPlatform = await createPlatform(platformData);
      dispatch(createPlatformSuccess(newPlatform));
      return {
        success: true,
        data: newPlatform,
        error: null,
      };
    } catch (error) {
      return {
        success: false,
        data: null,
        error: {
          message: error.message || 'Failed to create platform',
          response: error.response?.data,
          status: error.response?.status,
        },
      };
    }
  };
}

function addCompanyLicenses(companyName, licenseData, isLegacy = false) {
  return async (dispatch) => {
    dispatch(updateSavingStatuses({ addLicensesSavingStatus: RequestStatus.IN_PROGRESS }));

    try {
      if (isLegacy) {
        await addLegacyLicenses(companyName, licenseData);
      } else {
        // For company catalog, build array format for v2 endpoint
        const catalogList = licenseData.map(item => ({
          course_id: item.course_id,
          available: item.available ?? null,
        }));

        await addCompanyCatalogItems(companyName, catalogList);
      }

      // Refresh license data after adding
      if (isLegacy) {
        await dispatch(fetchLegacyLicenses(companyName));
      } else {
        await dispatch(fetchCompanyCatalog(companyName));
      }
      dispatch(updateSavingStatuses({ addLicensesSavingStatus: RequestStatus.SUCCESSFUL }));
    } catch (error) {
      logStudioHomeError('studio_home_licenses_add_failed', error, { licenseCount: licenseData.length });
      dispatch(updateSavingStatuses({ addLicensesSavingStatus: RequestStatus.FAILED }));
      throw error;
    }
  };
}

function updatePlatformThunk(platformData) {
  return async (dispatch) => {
    try {
      const updatedPlatform = await updatePlatform(platformData);
      dispatch(updatePlatformSuccess(updatedPlatform));
      return {
        success: true,
        data: updatedPlatform,
        error: null,
      };
    } catch (error) {
      return {
        success: false,
        data: null,
        error: {
          message: error.message || 'Failed to update platform',
          response: error.response?.data,
          status: error.response?.status,
        },
      };
    }
  };
}

function removeCompanyLicenses(companyName, licenseData, isLegacy = false) {
  return async (dispatch) => {
    dispatch(updateSavingStatuses({ removeLicensesSavingStatus: RequestStatus.IN_PROGRESS }));

    try {
      if (isLegacy) {
        await removeLegacyLicenses(companyName, licenseData);
      } else {
        // For company catalog, use the DELETE endpoint to remove items
        await deleteCompanyCatalogItems(companyName, licenseData);
      }

      // Refresh license data after removing
      if (isLegacy) {
        await dispatch(fetchLegacyLicenses(companyName));
      } else {
        await dispatch(fetchCompanyCatalog(companyName));
      }
      dispatch(updateSavingStatuses({ removeLicensesSavingStatus: RequestStatus.SUCCESSFUL }));
    } catch (error) {
      logStudioHomeError('studio_home_licenses_remove_failed', error, { licenseCount: licenseData.length });
      dispatch(updateSavingStatuses({ removeLicensesSavingStatus: RequestStatus.FAILED }));
      throw error;
    }
  };
}
function fetchProgramData(platformKey) {
  return async (dispatch) => {
    dispatch(updateLoadingStatuses({ programLoadingStatus: RequestStatus.IN_PROGRESS }));

    try {
      const programData = await getPrograms(platformKey);
      dispatch(fetchProgramDataSuccess(programData));
      dispatch(updateLoadingStatuses({ programLoadingStatus: RequestStatus.SUCCESSFUL }));
    } catch (error) {
      dispatch(updateLoadingStatuses({ programLoadingStatus: RequestStatus.FAILED }));
    }
  };
}

function updateCompanyLicenseQuantities(companyName, updateData, isLegacy = false) {
  return async (dispatch) => {
    dispatch(updateSavingStatuses({ updateLicensesSavingStatus: RequestStatus.IN_PROGRESS }));

    try {
      if (isLegacy) {
        // For legacy, we need to call add-licenses with updated quantities (delta-based)
        await addLegacyLicenses(companyName, updateData);
      } else {
        // For company catalog, get current catalog data first
        const currentCatalog = await getCompanyCatalog(companyName);
        const catalogList = [];

        updateData.forEach(item => {
          // Get current available value; null means unlimited, undefined means not yet in catalog (treat as 0)
          const rawAvailable = currentCatalog?.catalog?.[item.course_id]?.available;
          const currentAvailable = rawAvailable === null ? null : (rawAvailable ?? 0);
          // Calculate new absolute value by adding delta to current value; null delta means set to unlimited
          let newAvailable = item.delta;
          if (item.delta != null && currentAvailable !== null) {
            newAvailable = currentAvailable + item.delta;
          }
          const clampedAvailable = newAvailable && newAvailable <= 0 ? 0 : newAvailable;

          // Use pk-based update for existing entries, course_id-based for new entries
          const pk = currentCatalog?.catalog?.[item.course_id]?.id;
          catalogList.push(pk
            ? { id: pk, available: clampedAvailable }
            : { course_id: item.course_id, available: clampedAvailable });
        });

        await patchCompanyCatalog(companyName, catalogList);
      }

      // Refresh license data after updating
      if (isLegacy) {
        await dispatch(fetchLegacyLicenses(companyName));
      } else {
        await dispatch(fetchCompanyCatalog(companyName));
      }
      dispatch(updateSavingStatuses({ updateLicensesSavingStatus: RequestStatus.SUCCESSFUL }));
    } catch (error) {
      logStudioHomeError('studio_home_license_quantities_update_failed', error, {
        licenseCount: updateData.length,
      });
      dispatch(updateSavingStatuses({ updateLicensesSavingStatus: RequestStatus.FAILED }));
      throw error;
    }
  };
}

function createProgramThunk(programData) {
  return async (dispatch) => {
    try {
      const newProgram = await createProgram(programData);
      dispatch(createProgramSuccess(newProgram));
      // Re-fetch the full programs list to ensure the dropdown stays in sync with server
      dispatch(fetchProgramData(programData.platform_key));
      return {
        success: true,
        data: newProgram,
        error: null,
      };
    } catch (error) {
      return {
        success: false,
        data: null,
        error: {
          message: error.message || 'Failed to create program',
          response: error.response?.data,
          status: error.response?.status,
        },
      };
    }
  };
}

function updateProgramThunk(programData) {
  return async (dispatch) => {
    try {
      const updatedProgram = await updateProgram(programData);
      dispatch(updateProgramSuccess(updatedProgram));
      return {
        success: true,
        data: updatedProgram,
        error: null,
      };
    } catch (error) {
      return {
        success: false,
        data: null,
        error: {
          message: error.message || 'Failed to update program',
          response: error.response?.data,
          status: error.response?.status,
        },
      };
    }
  };
}

function deleteProgramThunk(org, itemId) {
  return async (dispatch) => {
    try {
      await deleteProgram(org, itemId);
      dispatch(deleteProgramSuccess({ itemId }));
      return {
        success: true,
        data: { itemId },
        error: null,
      };
    } catch (error) {
      return {
        success: false,
        data: null,
        error: {
          message: error.message || 'Failed to delete program',
          response: error.response?.data,
          status: error.response?.status,
        },
      };
    }
  };
}

function refreshAdminPortalCache(companyName) {
  return async (dispatch) => {
    dispatch(updateSavingStatuses({ refreshAdminPortalCacheSavingStatus: RequestStatus.PENDING }));

    try {
      await triggerAdminPortalCacheRefresh(companyName);
      dispatch(updateSavingStatuses({ refreshAdminPortalCacheSavingStatus: RequestStatus.SUCCESSFUL }));
      return { success: true };
    } catch (error) {
      dispatch(updateSavingStatuses({ refreshAdminPortalCacheSavingStatus: RequestStatus.FAILED }));
      throw error;
    }
  };
}

function runCourseAggregationThunk() {
  return async (dispatch) => {
    dispatch(updateSavingStatuses({ runCourseAggregationStatus: RequestStatus.PENDING }));

    try {
      await runCourseAggregation();
      dispatch(updateSavingStatuses({ runCourseAggregationStatus: RequestStatus.SUCCESSFUL }));
      return { success: true };
    } catch (error) {
      dispatch(updateSavingStatuses({ runCourseAggregationStatus: RequestStatus.FAILED }));
      throw error;
    }
  };
}

/**
 * Fetch company cached_data - stores complete API response
 * Returns empty object on 404
 */
function fetchCompanyCachedDataSpecific(companyName) {
  return async (dispatch, getState) => {
    const requestId = getCompanyCachedDataRequestId(companyName);
    dispatch(updateSavingStatuses({ fetchCompanyCachedDataStatus: RequestStatus.IN_PROGRESS }));
    dispatch(initializeCompanyCachedData({ companyName, requestId }));
    dispatch(syncSelectedCompanyCachedData(companyName));

    try {
      await hydrateNonPaginatedCompanyCachedDataEndpoint(
        dispatch,
        companyName,
        'totalLearningOverview',
        requestId,
      );

      await hydratePaginatedCompanyCachedDataEndpoint(
        dispatch,
        getState,
        companyName,
        'skillsDashboard',
        requestId,
        { complete: false },
      );

      dispatch(fetchSpecificCompanyCachedDataSuccess({
        companyName,
        requestId,
        hydrated: false,
      }));
      dispatch(syncSelectedCompanyCachedData(companyName));
      dispatch(updateSavingStatuses({ fetchCompanyCachedDataStatus: RequestStatus.SUCCESSFUL }));

      (async () => {
        try {
          await hydratePaginatedCompanyCachedDataEndpoint(
            dispatch,
            getState,
            companyName,
            'skillsDashboard',
            requestId,
            { complete: true },
          );

          for (const endpoint of COMPANY_CACHED_DATA_ENDPOINT_FETCH_ORDER) {
            if (endpoint !== 'totalLearningOverview' && endpoint !== 'skillsDashboard') {
              if (PAGINATED_COMPANY_CACHED_DATA_ENDPOINTS.has(endpoint)) {
                // eslint-disable-next-line no-await-in-loop
                await hydratePaginatedCompanyCachedDataEndpoint(
                  dispatch,
                  getState,
                  companyName,
                  endpoint,
                  requestId,
                  { complete: true },
                );
              } else {
                // eslint-disable-next-line no-await-in-loop
                await hydrateNonPaginatedCompanyCachedDataEndpoint(dispatch, companyName, endpoint, requestId);
              }
            }
          }

          dispatch(fetchSpecificCompanyCachedDataSuccess({ companyName, requestId, hydrated: true }));
          dispatch(syncSelectedCompanyCachedData(companyName));
        } catch (backgroundError) {
          logStudioHomeError('studio_home_cached_data_background_hydration_failed', backgroundError);
          dispatch(fetchSpecificCompanyCachedDataFailure({
            companyName,
            requestId,
            error: backgroundError.message || 'Failed to hydrate company cached data',
          }));
        }
      })();

      return true;
    } catch (error) {
      // Handle 404 gracefully - company catalog doesn't exist (not migrated yet)
      // Return empty object so components can infer "not a company catalog organization"
      if (error.response?.status === 404 || error.customAttributes?.httpErrorStatus === 404) {
        dispatch(fetchSpecificCompanyCachedDataSuccess({ companyName, requestId, hydrated: true }));
        dispatch(syncSelectedCompanyCachedData(companyName));
        dispatch(updateSavingStatuses({ fetchCompanyCachedDataStatus: RequestStatus.SUCCESSFUL }));
        return {};
      }

      // For other errors, fail as normal
      logStudioHomeError('studio_home_cached_data_fetch_failed', error);
      dispatch(fetchSpecificCompanyCachedDataFailure({
        companyName,
        requestId,
        error: error.message || 'Failed to fetch cached company data',
      }));
      dispatch(updateSavingStatuses({ fetchCompanyCachedDataStatus: RequestStatus.FAILED }));
      throw error;
    }
  };
}

/**
 * Derive the admin list from already-fetched companyEmployees state.
 * Filters for is_admin === true — no additional API call needed.
 */
function fetchCompanyAdmins() {
  return (dispatch, getState) => {
    const employees = getState().studioHome.companyEmployees;
    const admins = Array.isArray(employees)
      ? employees.filter((e) => e.is_admin === true)
      : [];
    dispatch(fetchCompanyAdminsSuccess(admins));
  };
}

/**
 * Fetch company employees
 * Returns empty array on 404
 */
function fetchCompanyEmployees(companyName, page = 1, pageSize = 10, force = false) {
  return async (dispatch, getState) => {
    dispatch(updateSavingStatuses({ fetchCompanyEmployeeStatus: RequestStatus.IN_PROGRESS }));

    try {
      const cachedPage = getState().studioHome.companyEmployeePagesByOrg[companyName]?.pages?.[page];
      const cachedPageSize = getState().studioHome.companyEmployeePagesByOrg[companyName]?.pageSize;
      const employeePage = !force && cachedPage && cachedPageSize === pageSize
        ? {
          ...cachedPage,
          count: getState().studioHome.companyEmployeePagesByOrg[companyName].count,
        }
        : await loadCompanyEmployeePage(companyName, page, pageSize);
      const employees = employeePage.results;
      const pagePayload = {
        ...employeePage, companyName, page, pageSize,
      };
      if (getState().studioHome.selectedCompanyOrPlatformKey === companyName) {
        dispatch(fetchCompanyEmployeePageSuccess(pagePayload));
      } else {
        dispatch(storeCompanyEmployeePage(pagePayload));
      }
      // eslint-disable-next-line @typescript-eslint/no-use-before-define
      await dispatch(fetchAllLearnerEnrollmentsInBackground(companyName, employees));
      dispatch(updateSavingStatuses({ fetchCompanyEmployeeStatus: RequestStatus.SUCCESSFUL }));
      return employeePage;
    } catch (error) {
      if (error.response?.status === 404 || error.customAttributes?.httpErrorStatus === 404) {
        const emptyPagePayload = {
          companyName, count: 0, page, pageSize, results: [],
        };
        if (getState().studioHome.selectedCompanyOrPlatformKey === companyName) {
          dispatch(fetchCompanyEmployeePageSuccess(emptyPagePayload));
        } else {
          dispatch(storeCompanyEmployeePage(emptyPagePayload));
        }
        dispatch(updateSavingStatuses({ fetchCompanyEmployeeStatus: RequestStatus.SUCCESSFUL }));
        return { count: 0, results: [] };
      }

      // For other errors, fail as normal
      logStudioHomeError('studio_home_employees_fetch_failed', error);
      dispatch(updateSavingStatuses({ fetchCompanyEmployeeStatus: RequestStatus.FAILED }));
      throw error;
    }
  };
}

function fetchRemainingCompanyEmployeeEnrollments(companyName, startPage = 2, pageSize = 10) {
  return async (dispatch, getState) => {
    let page = startPage;

    try {
      while (page !== null) {
        // eslint-disable-next-line no-await-in-loop
        const employeePage = await loadCompanyEmployeePage(companyName, page, pageSize);
        dispatch(storeCompanyEmployeePage({
          ...employeePage, companyName, page, pageSize,
        }));
        if (getState().studioHome.selectedCompanyOrPlatformKey === companyName) {
          dispatch(appendCompanyAdmins(employeePage.results.filter((employee) => employee.is_admin === true)));
        }
        // eslint-disable-next-line @typescript-eslint/no-use-before-define, no-await-in-loop
        await dispatch(fetchAllLearnerEnrollmentsInBackground(companyName, employeePage.results));
        page = employeePage.nextPage;
      }
    } catch (error) {
      logStudioHomeError('studio_home_background_employee_enrollments_fetch_failed', error);
    }
  };
}

function refreshCompanyEmployees(companyName, pageSize = 10) {
  return async (dispatch) => {
    dispatch(clearCompanyEmployeePages(companyName));
    const employeePage = await dispatch(fetchCompanyEmployees(companyName, 1, pageSize, true));
    if (employeePage?.nextPage) {
      dispatch(fetchRemainingCompanyEmployeeEnrollments(companyName, employeePage.nextPage, pageSize));
    }
    return employeePage;
  };
}

/**
 * Fetch enrollment enrichment (courses, certificate, grade, progress) for a single batch of
 * learners (identified by email) via v1/dashboard/enrollments/bulk/. Batches are kept small (see
 * LEARNER_ENROLLMENTS_BATCH_SIZE) to keep each request fast.
 */
function fetchLearnerEnrollmentsBatch(companyName, learnerIdentifiers, include = ['certificate', 'grade', 'last_active']) {
  return async (dispatch) => {
    const usernames = learnerIdentifiers.map(({ username }) => username).filter(Boolean);
    if (!usernames.length) {
      return;
    }

    dispatch(setLearnerEnrollmentBatchLoading({ companyName, usernames, include }));

    try {
      const learners = learnerIdentifiers
        .filter(({ email }) => Boolean(email))
        .map(({ email }) => ({ email }));
      if (!learners.length) {
        dispatch(storeLearnerEnrollmentBatchFailure({
          companyName,
          usernames,
          error: new Error('Learner enrollment lookup requires an email address.'),
        }));
        return;
      }
      const response = await getBulkLearnerEnrollments(companyName, learners, { include });
      dispatch(storeLearnerEnrollmentBatchSuccess({
        companyName,
        results: response?.results || [],
        include,
        identifiers: learnerIdentifiers,
      }));
    } catch (error) {
      logStudioHomeError('studio_home_learner_enrollments_batch_fetch_failed', error, {
        learnerCount: usernames.length,
      });
      dispatch(storeLearnerEnrollmentBatchFailure({
        companyName, usernames, error, include,
      }));
    }
  };
}

/**
 * Kick off background enrichment of every learner in the roster, in small sequential
 * batches, skipping any learner whose enrollment data has already been fetched
 * (or is currently in flight) for this org. Intended to be dispatched once the roster
 * (getCompanyEmployees) is loaded, without blocking the initial table render.
 */
function fetchAllLearnerEnrollmentsInBackground(companyName, learners) {
  return async (dispatch, getState) => {
    dispatch(initializeLearnerEnrollmentsForOrg(companyName));

    const existing = getState().studioHome.learnerEnrollmentsByOrg[companyName] || {};
    const pendingLearners = learners.filter(({ username }) => {
      const entry = existing[username];
      return !entry || entry.loadingStatus === RequestStatus.FAILED;
    });

    const batches = chunkArray(pendingLearners, LEARNER_ENROLLMENTS_BATCH_SIZE);

    // Sequential, low-priority: don't await Promise.all across batches, so the UI thread/
    // network isn't saturated with a burst of concurrent requests for large rosters.
    // eslint-disable-next-line no-restricted-syntax
    for (const batch of batches) {
      // eslint-disable-next-line no-await-in-loop
      await dispatch(fetchLearnerEnrollmentsBatch(companyName, batch));
    }
  };
}

function fetchLearnerEnrollmentProgress(companyName, learner) {
  return fetchLearnerEnrollmentsBatch(companyName, [learner], ['progress']);
}

/** Refresh enrollment enrichment only for learners affected by a mutation. */
function refreshLearnerEnrollments(companyName, usernames) {
  return async (dispatch, getState) => {
    const uniqueUsernames = [...new Set(usernames.filter(Boolean))];
    if (!uniqueUsernames.length) {
      return;
    }

    dispatch(initializeLearnerEnrollmentsForOrg(companyName));
    dispatch(invalidateLearnerEnrollments({ companyName, usernames: uniqueUsernames }));

    const employees = getState().studioHome.companyEmployees || [];
    const learnersByUsername = new Map(
      (Array.isArray(employees) ? employees : []).map((employee) => [employee.username, employee]),
    );
    const batches = chunkArray(
      uniqueUsernames.map((username) => learnersByUsername.get(username) || { username }),
      LEARNER_ENROLLMENTS_BATCH_SIZE,
    );
    // Keep mutation refreshes as controlled sequential requests, like initial hydration.
    // eslint-disable-next-line no-restricted-syntax
    for (const batch of batches) {
      // eslint-disable-next-line no-await-in-loop
      await dispatch(fetchLearnerEnrollmentsBatch(companyName, batch));
    }
  };
}

export {

  fetchStudioHomeData,
  fetchLibraryData,
  fetchFullCourseList,
  fetchPlatformData,
  createPlatformThunk,
  updatePlatformThunk,
  fetchProgramData,
  createProgramThunk,
  updateProgramThunk,
  deleteProgramThunk,
  requestCourseCreatorQuery,
  handleDeleteNotificationQuery,
  fetchCompanyManagementGeneralData,
  fetchCompanyManagementSpecificData,
  createNewCompany,
  updateCompanyField,
  updateCustomCsvFields,
  updateCustomAdminPortalFeatures,
  manageCompanyAdmins,
  fetchCompanyCatalog,
  fetchLegacyLicenses,
  fetchAllCompanyLicenseData,
  addCompanyLicenses,
  removeCompanyLicenses,
  updateCompanyLicenseQuantities,
  refreshAdminPortalCache,
  runCourseAggregationThunk,
  fetchCompanyCachedDataEndpointPage,
  fetchCompanyCachedDataSpecific,
  fetchCompanyEmployees,
  fetchRemainingCompanyEmployeeEnrollments,
  refreshCompanyEmployees,
  fetchCompanyAdmins,
  fetchLearnerEnrollmentsBatch,
  fetchLearnerEnrollmentProgress,
  fetchAllLearnerEnrollmentsInBackground,
  refreshLearnerEnrollments,
};
